import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import SearchIcon from "@mui/icons-material/Search";
import {
  Chip,
  FormControl,
  FormHelperText,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import { DateTimePicker } from "@mui/x-date-pickers";
import dayjs from "dayjs";
import { useFormikContext } from "formik";

import { MDButton } from "@/components/atoms/MDButton";
import { IExecutionLog } from "@/interfaces/execution-log.interfaces";
import { ExecutionLogFiltersMessages } from "@/messages/campaigns/execution-log-filters.messages";
import { FilterType, FilterValue, IFilter } from "@/types/filter.types";

import {
  FILTER_ATTRIBUTES,
  FILTER_OPTIONS,
  FILTER_OPTION_VALUES,
  STATUS,
  getInitialFilterValues,
} from "../../constants";
import { ExecutionLogFilterKey } from "../../types";

type Props = {
  messages: ExecutionLogFiltersMessages;
  filters: IFilter<IExecutionLog>[];
  addNewFilter: (filter: IFilter<IExecutionLog>) => void;
  deleteFilter: (index: number) => void;
  campaignId: number;
};

function FiltersForm({ messages, filters, addNewFilter, deleteFilter, campaignId }: Props) {
  const { filterByLabel, valueLabel, search, filterButton } = messages;
  const { values, setValues, submitForm, errors } = useFormikContext<IFilter<IExecutionLog>[]>();

  const currentProperty = values[0].property === "campaignId" ? "status" : values[0].property;

  const handleChangeFilterProperty = (newProperty: ExecutionLogFilterKey) => {
    const { type: filterType = FilterType.Contains, defaultValue: value = "" } =
      FILTER_ATTRIBUTES[newProperty] || {};

    setValues([{ property: newProperty, value, filterType }]);
  };

  const handleChangeFilterValue = (newValue: FilterValue) => {
    if (currentProperty === "dateSent" && newValue && typeof newValue === "string") {
      const startDate = dayjs(newValue).startOf("day").toISOString();

      const endDate = dayjs(newValue).endOf("day").toISOString();

      setValues([
        {
          property: "dateSent",
          value: startDate,
          filterType: FilterType.GreaterThan,
          label: "Data inicial do log",
        },
        {
          property: "dateSent",
          value: endDate,
          filterType: FilterType.LessThan,
          label: "Data final do log",
        },
      ]);
    } else {
      const updatedValues = [{ ...values[0], value: newValue }];
      setValues(updatedValues);
    }
  };

  type PropertyValueType = string;

  const findLabelForProperty = (propertyValue: PropertyValueType): string => {
    const options = FILTER_OPTIONS(messages);
    const foundOption = options.find((option) => option.value === propertyValue);

    return foundOption ? foundOption.label : propertyValue.toString();
  };

  const handleAddTemporaryFilter = () => {
    if (currentProperty === "all" || !values[0].value) return;

    if (currentProperty === "dateSent") {
      const filtersWithLabel = values.map((value) => ({
        ...value,
        label: findLabelForProperty(String(value.property)),
      }));
      filtersWithLabel.forEach((filter) => addNewFilter(filter));
    } else {
      const filterWithLabel = {
        ...values[0],
        label: findLabelForProperty(String(values[0].property)),
      };
      addNewFilter(filterWithLabel);
    }

    setValues(getInitialFilterValues(campaignId));
  };

  const handleRemoveTemporaryFilter = (index: number) => {
    deleteFilter(index);
  };

  const getFilterOptions = () => {
    let options = FILTER_OPTIONS(messages);
    if (filters.length > 0) {
      options = options.filter((option) => option.value !== "all");
    }
    return options;
  };

  const createAllFilter = () => ({
    property: "all" as ExecutionLogFilterKey,
    value: "",
    filterType: FilterType.Contains,
  });

  const handleSubmitFilters = (applyAll: boolean) => {
    // Se applyAll for verdadeiro, cria apenas o filtro "all", senão, usa os filtros temporários.
    let filtersToApply = applyAll ? [createAllFilter()] : [...filters];

    // Verifica se existe algum filtro com a propriedade "campaignId".
    filtersToApply = filtersToApply.map((filter) => {
      if (filter.property === "campaignId") {
        // Se encontrar "campaignId", substitui por "status".
        return {
          property: "status",
          value: filter.value, // Você pode ajustar o valor ou definir um valor padrão aqui, se necessário.
          filterType: FilterType.Equals,
        };
      }
      return filter;
    });

    // Sempre adicione o filtro de campaignId no início do array
    const campaignFilter = {
      property: "campaignId",
      value: campaignId,
      filterType: FilterType.Equals,
    };

    // Inclui o filtro da campanha junto com os outros.
    setValues([campaignFilter, ...filtersToApply]);
    submitForm();
  };

  const filterValueType = FILTER_ATTRIBUTES[currentProperty]?.valueType;
  const isSelectType = filterValueType === "boolean" || filterValueType === "options";
  const isNumber = filterValueType === "number";
  const isTextType = filterValueType === "string" || isNumber;

  const optionValues = FILTER_OPTION_VALUES(messages)[currentProperty];

  const isPropertyAll = currentProperty === "all";
  const hasValidValue = values[0].value;
  const isFilterPresentForProperty = filters.some((filter) => filter.property === currentProperty);
  const isDateField = currentProperty === "dateSent";
  const isAddButtonEnabled = !isPropertyAll && hasValidValue && !isFilterPresentForProperty;
  const isSubmitButtonDisabled = filters.length === 0 && !isPropertyAll;

  const formatDate = (dateValue: string) => {
    return dayjs(dateValue).format("DD/MM/YYYY");
  };

  const getOrderStatusLabel = (value: FilterValue) => {
    const options = STATUS(messages);
    const foundOption = options.find((option) => option.value === value);
    return foundOption ? foundOption.label : value;
  };

  const formatFilterValue = (filter: IFilter<IExecutionLog>) => {
    switch (filter.property) {
      case "dateSent":
        return formatDate(filter.value.toString());
      case "status":
        return getOrderStatusLabel(filter.value);
      default:
        return filter.value;
    }
  };

  return (
    <Grid container spacing={3} size={12}>
      <Grid container size={12}>
        <Grid
          sx={{ maxWidth: 250 }}
          size={{
            lg: isPropertyAll ? 9 : 4.5,
            md: isPropertyAll ? 8 : 4,
            sm: isPropertyAll ? 8 : 4,
            xs: 12,
          }}
        >
          <FormControl fullWidth variant="outlined" error={!!errors[0] && !!errors[0].property}>
            <InputLabel>{filterByLabel}</InputLabel>
            <Select
              value={currentProperty}
              onChange={(e) => handleChangeFilterProperty(e.target.value as ExecutionLogFilterKey)}
              label={filterByLabel}
            >
              {getFilterOptions().map((option, index) => (
                <MenuItem
                  value={option.value}
                  key={`${index}-${option.value}`}
                  disabled={option.disabled}
                >
                  {option.label}
                </MenuItem>
              ))}
            </Select>
            {!!errors[0] && !!errors[0].property && (
              <FormHelperText>{errors[0].property}</FormHelperText>
            )}
          </FormControl>
        </Grid>

        {!isPropertyAll && (
          <Grid
            sx={{ maxWidth: 250 }}
            size={{
              lg: 4.5,
              md: 4,
              sm: 4,
              xs: 12,
            }}
          >
            {isSelectType && (
              <FormControl fullWidth variant="outlined" error={!!errors[0] && !!errors[0].value}>
                <InputLabel id="value-label">{valueLabel}</InputLabel>
                <Select
                  labelId="value-label"
                  value={values[0].value}
                  onChange={(e) => handleChangeFilterValue(e.target.value)}
                  label={valueLabel}
                  name="value"
                >
                  {optionValues?.map((option, index) => (
                    <MenuItem value={String(option.value)} key={`${index}-${option.value}`}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
                {!!errors[0] && errors[0].value && (
                  <FormHelperText>{errors[0].value}</FormHelperText>
                )}
              </FormControl>
            )}
            {isTextType && (
              <TextField
                name="value"
                onChange={(e) => handleChangeFilterValue(e.target.value)}
                placeholder={search}
                value={values[0].value}
                error={!!errors[0] && !!errors[0].value}
                helperText={!!errors[0] && errors[0].value}
                fullWidth
                disabled={isPropertyAll}
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position="end">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  },
                }}
              />
            )}
            {isDateField && (
              <DateTimePicker
                label={valueLabel}
                value={dayjs(typeof values[0].value === "string" ? values[0].value : new Date())}
                onChange={(value) => handleChangeFilterValue(value ? value.toISOString() : "")}
                slotProps={{
                  textField: {
                    helperText: errors[0]?.value,
                    error: Boolean(errors[0]?.value),
                    fullWidth: true,
                    InputLabelProps: { shrink: true },
                  },
                }}
              />
            )}
          </Grid>
        )}

        <Grid
          container
          justifyContent="flex-start"
          spacing={2}
          size={{
            lg: 3,
            md: 4,
            sm: 4,
            xs: 12,
          }}
        >
          <Grid>
            <MDButton
              variant="outlined"
              color="dark"
              onClick={handleAddTemporaryFilter}
              disabled={!isAddButtonEnabled}
            >
              <AddCircleOutlineIcon />
            </MDButton>
          </Grid>
          <Grid>
            <MDButton
              variant="contained"
              color="dark"
              onClick={() => handleSubmitFilters(currentProperty === "all")}
              disabled={isSubmitButtonDisabled}
            >
              {filterButton}
            </MDButton>
          </Grid>
        </Grid>
      </Grid>
      <Grid container spacing={2} size={12}>
        {filters.map((filter, index) => (
          <Grid key={index}>
            <Chip
              label={`${filter.label}: ${formatFilterValue(filter)}`}
              onDelete={() => handleRemoveTemporaryFilter(index)}
            />
          </Grid>
        ))}
      </Grid>
    </Grid>
  );
}

export default FiltersForm;
